import {
  DialogueLine,
  MediaRef,
  ShotStatus,
  ShotView,
  type Command,
  type StoryboardView,
} from "@dramapie/shared";

import { mediaSlotFromEventKind, type MediaSlot } from "./generations";

/**
 * 命令的**本地乐观投影**。
 *
 * 只负责让界面立刻反映用户的意图；真正的 rev、id 生成、校验都以服务端为准。
 * 服务端成功返回后只对齐 `rev`（契约要求前端不要自己推演结果），
 * 409 时用响应里的 `view` 整体覆盖。
 */

export interface ProjectEventLike {
  type: string;
  rev: number;
  payload: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isDialogueArray(value: unknown): value is DialogueLine[] {
  const parsed = DialogueLine.array().safeParse(value);
  return parsed.success;
}

function withStats(view: StoryboardView): StoryboardView {
  return {
    ...view,
    stats: {
      ...view.stats,
      shotCount: view.shots.length,
      approvedCount: view.shots.filter((shot) => shot.status === "approved").length,
    },
  };
}

function patchShot(shot: ShotView, patch: Record<string, unknown>): ShotView {
  let next: ShotView = { ...shot };

  if (typeof patch.framing === "string") next.framing = patch.framing;
  if (typeof patch.duration === "number" && Number.isFinite(patch.duration)) {
    next.duration = patch.duration;
  }
  if (typeof patch.action === "string") next.action = patch.action;
  const status = ShotStatus.safeParse(patch.status);
  if (status.success) next.status = status.data;

  const charIds = patch.charIds ?? patch.chars;
  if (Array.isArray(charIds)) {
    next.chars = charIds.filter((value): value is string => typeof value === "string");
  }
  if (isDialogueArray(patch.dialogue)) next.dialogue = patch.dialogue;

  return next;
}

function removeShot(view: StoryboardView, shotId: string): StoryboardView {
  return withStats({
    ...view,
    shots: view.shots.filter((shot) => shot.id !== shotId),
    scenes: view.scenes.map((scene) =>
      scene.shotIds.includes(shotId)
        ? { ...scene, shotIds: scene.shotIds.filter((id) => id !== shotId) }
        : scene,
    ),
  });
}

/**
 * 场次内重排：镜头 order 是**全片连续序号**，所以重排一场后
 * 要把所有场次按顺序重新编号，而不是只改这一场内部的数字。
 */
function reorderShots(view: StoryboardView, sceneId: string, orderedIds: readonly string[]): StoryboardView {
  const known = new Set(view.shots.map((shot) => shot.id));
  const sequences = new Map<string, string[]>();

  for (const scene of [...view.scenes].sort((a, b) => a.order - b.order)) {
    const existing = view.shots
      .filter((shot) => shot.sceneId === scene.id)
      .sort((a, b) => a.order - b.order)
      .map((shot) => shot.id);

    if (scene.id !== sceneId) {
      sequences.set(scene.id, existing);
      continue;
    }

    const next: string[] = [];
    for (const id of orderedIds) {
      if (known.has(id) && !next.includes(id)) next.push(id);
    }
    // 服务端要求集合完全一致；本地兜底把漏掉的镜头补回尾部，避免丢镜。
    for (const id of existing) {
      if (!next.includes(id)) next.push(id);
    }
    sequences.set(scene.id, next);
  }

  const ordered: string[] = [];
  for (const ids of sequences.values()) ordered.push(...ids);
  const assigned = new Set(ordered);
  for (const shot of [...view.shots].sort((a, b) => a.order - b.order)) {
    if (!assigned.has(shot.id)) ordered.push(shot.id);
  }

  const orderById = new Map(ordered.map((id, index) => [id, index + 1]));
  return withStats({
    ...view,
    shots: view.shots.map((shot) => {
      const order = orderById.get(shot.id);
      return order === undefined ? shot : { ...shot, order };
    }),
    scenes: view.scenes.map((scene) => {
      const ids = sequences.get(scene.id);
      return ids ? { ...scene, shotIds: ids } : scene;
    }),
  });
}

export function applyCommandsLocally(
  view: StoryboardView,
  commands: readonly Command[],
): StoryboardView {
  let next = view;

  for (const command of commands) {
    switch (command.type) {
      case "update_shot": {
        next = withStats({
          ...next,
          shots: next.shots.map((shot) =>
            shot.id === command.shotId ? patchShot(shot, command.patch) : shot,
          ),
        });
        break;
      }
      case "set_dialogue": {
        next = withStats({
          ...next,
          shots: next.shots.map((shot) =>
            shot.id === command.shotId ? { ...shot, dialogue: command.lines } : shot,
          ),
        });
        break;
      }
      case "remove_shot": {
        next = removeShot(next, command.shotId);
        break;
      }
      case "reorder_shots": {
        next = reorderShots(next, command.sceneId, command.orderedIds);
        break;
      }
      case "update_scene": {
        next = {
          ...next,
          scenes: next.scenes.map((scene) =>
            scene.id === command.sceneId ? { ...scene, ...command.patch } : scene,
          ),
        };
        break;
      }
      case "add_shot": {
        // 新 id 由服务端生成，本地不猜；提交成功后等下一次查询 / 事件补齐。
        break;
      }
    }
  }

  return next;
}

/** 把 `media.ready` 里的媒体挂到对应镜头的槽位上。 */
function withMedia(shot: ShotView, slot: MediaSlot, media: MediaRef): ShotView {
  if (slot === "keyframe") return { ...shot, keyframe: media };
  if (slot === "endframe") return { ...shot, endFrame: media };
  return { ...shot, clip: media };
}

/** 把 WebSocket 增量事件合并进本地故事板。未知 / agent 事件原样返回。 */
export function applyProjectEvent(
  view: StoryboardView,
  event: ProjectEventLike,
): StoryboardView {
  const payload = event.payload;

  if (event.type === "media.ready") {
    // 契约只定义事件名，payload 形状未冻结：兼容
    // `{ shot }`（整镜推送）与 `{ shotId, kind?, media }` 两种写法。
    const fullShot = ShotView.safeParse(payload.shot);
    if (fullShot.success) {
      const exists = view.shots.some((shot) => shot.id === fullShot.data.id);
      const shots = exists
        ? view.shots.map((shot) => (shot.id === fullShot.data.id ? fullShot.data : shot))
        : [...view.shots, fullShot.data];
      return withStats({ ...view, shots });
    }

    const media = MediaRef.safeParse(payload.media);
    const shotId =
      typeof payload.shotId === "string"
        ? payload.shotId
        : typeof payload.id === "string"
          ? payload.id
          : null;
    if (!media.success || !shotId) return view;

    const slot =
      mediaSlotFromEventKind(payload.kind) ?? (media.data.kind === "video" ? "clip" : "keyframe");
    return withStats({
      ...view,
      shots: view.shots.map((shot) =>
        shot.id === shotId ? withMedia(shot, slot, media.data) : shot,
      ),
    });
  }

  if (event.type === "shot.updated" && typeof payload.id === "string") {
    const patch = isRecord(payload.patch) ? payload.patch : {};
    return withStats({
      ...view,
      shots: view.shots.map((shot) =>
        shot.id === payload.id ? patchShot(shot, patch) : shot,
      ),
    });
  }

  if (event.type === "shot.deleted" && typeof payload.id === "string") {
    return removeShot(view, payload.id);
  }

  if (event.type === "shot.created") {
    const parsed = ShotView.safeParse(payload.shot);
    if (!parsed.success || view.shots.some((shot) => shot.id === parsed.data.id)) return view;
    return withStats({ ...view, shots: [...view.shots, parsed.data] });
  }

  if (event.type === "shot.reordered" && typeof payload.sceneId === "string") {
    const ids = Array.isArray(payload.orderedIds)
      ? payload.orderedIds.filter((id): id is string => typeof id === "string")
      : [];
    if (ids.length === 0) return view;
    return reorderShots(view, payload.sceneId, ids);
  }

  if (event.type === "scene.updated") {
    const sceneId =
      typeof payload.id === "string"
        ? payload.id
        : typeof payload.sceneId === "string"
          ? payload.sceneId
          : null;
    if (!sceneId) return view;
    const patch = isRecord(payload.patch) ? payload.patch : {};
    return {
      ...view,
      scenes: view.scenes.map((scene) => {
        if (scene.id !== sceneId) return scene;
        return {
          ...scene,
          ...(typeof patch.name === "string" ? { name: patch.name } : {}),
          ...(typeof patch.time === "string" ? { time: patch.time } : {}),
        };
      }),
    };
  }

  return view;
}
